import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Typography, Button, Tag, Space } from 'antd';
import { CalendarOutlined, EnvironmentOutlined, TeamOutlined } from '@ant-design/icons';
import { getContentWrapperStyle } from '../templateStyles';

const { Title, Text, Paragraph } = Typography;

export default function SectionEventList({ slug, section, siteInfo }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const events = section.content_data?.events || [];
  const brandColor = siteInfo?.brand_color || '#1890ff';
  const template = siteInfo?.site_template || 'modern';

  if (events.length === 0) return null;

  const isMinimal = template === 'minimal';
  const isClassic = template === 'classic';
  const cardRadius = isMinimal ? 0 : isClassic ? 4 : 12;
  const contentFont = getContentWrapperStyle(template, brandColor);

  const formatDate = (d) => d ? new Date(d).toLocaleString(i18n.language, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

  const isDeadlineSoon = (deadline) => {
    if (!deadline) return false;
    const diff = new Date(deadline) - new Date();
    return diff > 0 && diff < 3 * 24 * 60 * 60 * 1000;
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 24px', ...contentFont }}>
      <Title level={2} style={{
        textAlign: isMinimal ? 'left' : 'center',
        marginBottom: 8,
        fontWeight: isMinimal ? 300 : 600,
        letterSpacing: isMinimal ? 1 : 0,
        borderBottom: isMinimal ? `2px solid ${brandColor}` : 'none',
        paddingBottom: isMinimal ? 8 : 0,
        display: isMinimal ? 'inline-block' : 'block',
      }}>
        {section.title || t('sections.eventList.title')}
      </Title>
      {section.subtitle && <Paragraph style={{ textAlign: isMinimal ? 'left' : 'center', color: '#666', marginBottom: 32 }}>{section.subtitle}</Paragraph>}
      <Row gutter={[20, 20]}>
        {events.map(e => (
          <Col key={e.id} xs={24} md={12}>
            <Card
              hoverable
              onClick={() => navigate(`/s/${slug}/events`)}
              style={{
                borderTop: isMinimal ? 'none' : `3px solid ${brandColor}`,
                borderRadius: cardRadius,
                border: isMinimal ? '1px solid #e8e8e8' : undefined,
                borderTopColor: isMinimal ? '#e8e8e8' : brandColor,
              }}
            >
              {e.cover_image_url && (
                <div style={{ marginBottom: 12, borderRadius: isMinimal ? 0 : 6, overflow: 'hidden' }}>
                  <img src={e.cover_image_url} alt={e.title} style={{ width: '100%', display: 'block' }} />
                </div>
              )}
              <Title level={4} style={{ marginBottom: 8 }}>{e.title}</Title>
              <Space direction="vertical" size={4} style={{ marginBottom: 12 }}>
                {e.event_date && (
                  <Text type="secondary"><CalendarOutlined style={{ marginRight: 6 }} />{formatDate(e.event_date)}</Text>
                )}
                {e.location && (
                  <Text type="secondary"><EnvironmentOutlined style={{ marginRight: 6 }} />{e.location}</Text>
                )}
                {e.spots_left != null && (
                  <Text style={{ color: e.spots_left < 5 ? '#ff4d4f' : '#52c41a' }}>
                    <TeamOutlined style={{ marginRight: 6 }} />{t('sections.eventList.spotsLeft', { count: e.spots_left })}
                  </Text>
                )}
              </Space>
              {e.description && (
                <Paragraph ellipsis={{ rows: 2 }} style={{ color: '#666', marginBottom: 12 }}>{e.description}</Paragraph>
              )}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Button type="primary" style={{
                  background: brandColor,
                  borderColor: brandColor,
                  borderRadius: isMinimal ? 0 : isClassic ? 4 : undefined,
                }}>
                  {t('sections.eventList.register')}
                </Button>
                {e.registration_deadline && isDeadlineSoon(e.registration_deadline) && (
                  <Tag color="red">{t('sections.eventList.deadlineSoon')}</Tag>
                )}
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
}
